/**
 * React Hooks for Real-Time Notifications
 * 
 * Power of React + WebSocket for instant updates without any polling!
 * 
 * @version 1.0.0
 * @author CleverAct AI Team - React Real-Time Power
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { 
  realTimeNotifications, 
  type RealTimeNotification,
  type UnreadCountUpdate,
  type NewMessageNotification,
  type MessageReadNotification,
  type ConversationUpdate,
  type AgentPresenceUpdate
} from '../services/realTimeNotifications'
import { logger } from '../utils/logger'
import { useAuth } from './useAuth'
import { api } from '../services/api'
import { realTimeConversationsService } from '../services/realTimeConversations'

/**
 * Simple debounce utility function (not a hook) for WebSocket events
 * Creates a debounce function that prevents rapid duplicate state updates
 */
function createWebSocketDebouncer<T>(callback: (data: T) => void, delay: number = 150) {
  const timeouts = new Map<string, NodeJS.Timeout>()
  const pendingData = new Map<string, T>()
  
  return {
    debounce: (key: string, data: T) => {
      // Clear existing timeout for this key
      const existingTimeout = timeouts.get(key)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }
      
      // Store the latest data
      pendingData.set(key, data)
      
      // Set new timeout
      const newTimeout = setTimeout(() => {
        const latestData = pendingData.get(key)
        if (latestData) {
          callback(latestData)
          pendingData.delete(key)
          timeouts.delete(key)
        }
      }, delay)
      
      timeouts.set(key, newTimeout)
    },
    
    cleanup: () => {
      timeouts.forEach(timeout => clearTimeout(timeout))
      timeouts.clear()
      pendingData.clear()
    }
  }
}

/**
 * Main real-time notifications hook
 * Manages WebSocket connection and provides connection status
 */
export function useRealTimeNotifications() {
  const [isConnected, setIsConnected] = useState(realTimeNotifications.isConnected)
  const [connectionState, setConnectionState] = useState(realTimeNotifications.connectionState)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated, user } = useAuth()

  useEffect(() => {
    // Only proceed if user is authenticated and not on login page
    if (!isAuthenticated || !user) {
      logger.debug('🔌 Skipping WebSocket connection - user not authenticated')
      return
    }

    // Set up connection handlers
    realTimeNotifications.setEventHandlers({
      onConnectionChange: (connected) => {
        setIsConnected(connected)
        setConnectionState(realTimeNotifications.connectionState)
        if (connected) {
          setError(null)
          logger.info('🔌 Real-time notifications connected')
        }
      },
      onError: (errorMessage) => {
        setError(errorMessage)
        logger.error('❌ Real-time notification error', { error: errorMessage })
      }
    })

    // Connect if not already connected and user is authenticated
    if (!realTimeNotifications.isConnected) {
      logger.info('🔗 Starting real-time notifications for authenticated user...')
      // Add small delay during page refresh to ensure token is available
      setTimeout(() => {
        if (isAuthenticated && user && !realTimeNotifications.isConnected) {
          realTimeNotifications.connect()
        }
      }, 100) // Small delay to allow auth to fully initialize
    }

    // Update state with current status
    setIsConnected(realTimeNotifications.isConnected)
    setConnectionState(realTimeNotifications.connectionState)

    return () => {
      // Disconnect when user logs out
      if (!isAuthenticated) {
        logger.info('🔌 Disconnecting real-time notifications - user logged out')
        realTimeNotifications.disconnect()
      }
    }
  }, [isAuthenticated, user])

  const connect = useCallback(() => {
    realTimeNotifications.connect()
  }, [])

  const disconnect = useCallback(() => {
    realTimeNotifications.disconnect()
  }, [])

  return {
    isConnected,
    connectionState,
    lastUpdate,
    error,
    connect,
    disconnect
  }
}

/**
 * Hook for unread count updates
 * Eliminates the need for polling conversation lists!
 */
export function useUnreadCounts() {
  const [unreadCounts, setUnreadCounts] = useState<Map<string, number>>(new Map())
  const [totalUnread, setTotalUnread] = useState(0)
  const { isAuthenticated, user } = useAuth()

  useEffect(() => {
    // Clear counts when user logs out
    if (!isAuthenticated || !user) {
      setUnreadCounts(new Map())
      setTotalUnread(0)
      return
    }

    const unsubscribe = realTimeNotifications.addEventListener('unread_count_update', (data: UnreadCountUpdate) => {
      logger.debug('📊 Unread count update received', { data })
      
      setUnreadCounts(prev => {
        const newMap = new Map(prev)
        newMap.set(data.conversation_id, data.unread_count)
        return newMap
      })
      
      setTotalUnread(data.total_unread)
    })

    return unsubscribe
  }, [isAuthenticated, user])

  const getUnreadCount = useCallback((conversationId: string): number => {
    return unreadCounts.get(conversationId) || 0
  }, [unreadCounts])

  return {
    unreadCounts,
    totalUnread,
    getUnreadCount
  }
}

/**
 * Hook for real-time conversation updates
 * Eliminates the need for polling conversation lists!
 */
export function useConversationUpdates() {
  const [conversations, setConversations] = useState<Map<string, any>>(new Map())
  const [lastUpdatedConversation, setLastUpdatedConversation] = useState<string | null>(null)
  const { isAuthenticated, user } = useAuth()

  useEffect(() => {
    // Clear conversations when user logs out
    if (!isAuthenticated || !user) {
      setConversations(new Map())
      setLastUpdatedConversation(null)
      return
    }

    // Listen for new messages
    const unsubscribeNewMessage = realTimeNotifications.addEventListener('new_message', (data: NewMessageNotification) => {
      logger.debug('💬 New message received', { data })
      
      setConversations(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(data.conversation_id) || {}
        
        newMap.set(data.conversation_id, {
          ...existing,
          last_message: `${data.sender_type === 'user' ? 'Customer' : 'Agent'}: ${data.content}`,
          last_message_time: data.timestamp,
          updated_at: data.timestamp
        })
        
        return newMap
      })
      
      setLastUpdatedConversation(data.conversation_id)
    })

    // Listen for message read events
    const unsubscribeMessageRead = realTimeNotifications.addEventListener('messages_read', (data: MessageReadNotification) => {
      logger.debug('📖 Messages read', { data })
      // Handle read receipts if needed
    })

    // Listen for conversation updates
    const unsubscribeConversationUpdate = realTimeNotifications.addEventListener('conversation_update', (data: ConversationUpdate) => {
      // 🐛 DEBUG: Add special logging for resolved status to debug Close Chat issue
      if (data.status === 'resolved') {
        console.warn('🐛 DEBUG: RESOLVED conversation update received!', {
          conversationId: data.conversation_id,
          status: data.status,
          resolvedAt: (data as any).resolved_at,
          resolvedBy: (data as any).resolved_by,
          timestamp: data.timestamp,
          fullData: data
        })
      }
      
      logger.info('🔄 Conversation update received in useConversationUpdates hook', { 
        conversationId: data.conversation_id,
        status: data.status,
        assignedAgentId: data.assigned_agent_id,
        assignedAgentName: data.assigned_agent_name,
        fullData: data 
      })
      
      setConversations(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(data.conversation_id) || {}
        
        const updatedData = {
          ...existing,
          ...data,
          updated_at: new Date().toISOString()
        }
        
        newMap.set(data.conversation_id, updatedData)
        
        logger.info('📝 Updated conversation data in hook', {
          conversationId: data.conversation_id,
          updatedData
        })
        
        return newMap
      })
      
      setLastUpdatedConversation(data.conversation_id)
    })

    return () => {
      unsubscribeNewMessage()
      unsubscribeMessageRead()
      unsubscribeConversationUpdate()
    }
  }, [isAuthenticated, user])

  const getConversationUpdates = useCallback((conversationId: string) => {
    return conversations.get(conversationId)
  }, [conversations])

  return {
    conversations,
    lastUpdatedConversation,
    getConversationUpdates
  }
}

/**
 * Hook for agent presence updates
 */
export function useAgentPresence() {
  const [agentPresence, setAgentPresence] = useState<Map<string, boolean>>(new Map())

  useEffect(() => {
    const unsubscribe = realTimeNotifications.addEventListener('agent_presence_update', (data: AgentPresenceUpdate) => {
      logger.debug('👤 Agent presence update', { data })
      
      setAgentPresence(prev => {
        const newMap = new Map(prev)
        newMap.set(data.agent_id, data.is_online)
        return newMap
      })
    })

    return unsubscribe
  }, [])

  const isAgentOnline = useCallback((agentId: string): boolean => {
    return agentPresence.get(agentId) || false
  }, [agentPresence])

  return {
    agentPresence,
    isAgentOnline
  }
}

/**
 * Combined hook for conversation list with real-time updates
 * This replaces the polling-based useConversationList!
 */
export function useRealTimeConversationList() {
  const [conversations, setConversations] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated, user } = useAuth()
  
  // 🎯 Event deduplication to prevent processing same event multiple times
  const processedEventsRef = useRef<Set<string>>(new Set())
  const debouncerRef = useRef<ReturnType<typeof createWebSocketDebouncer> | null>(null)
  
  // 🎯 DEBOUNCED: Prevent rapid duplicate events that cause "refreshing" effect
  const handleConversationUpdate = useCallback((data: any) => {
    // Create event signature for deduplication 
    const eventSignature = `${data.type}-${data.conversation_id}-${data.timestamp || Date.now()}`
    
    // Skip if we've already processed this exact event recently
    if (processedEventsRef.current.has(eventSignature)) {
      logger.debug('🔄 Skipping duplicate event:', { eventSignature })
      return
    }
    
    // Mark as processed
    processedEventsRef.current.add(eventSignature)
    
    // Clean up old signatures every 100 events to prevent memory leaks
    if (processedEventsRef.current.size > 100) {
      const oldSignatures = Array.from(processedEventsRef.current).slice(0, 50)
      oldSignatures.forEach(sig => processedEventsRef.current.delete(sig))
    }
    
    logger.info('🔄 Processing debounced conversation update', {
      type: data.type,
      conversationId: data.conversation_id,
      eventSignature,
      timestamp: new Date().toISOString()
    })

    // 🎯 WhatsApp-style instant update: Directly update the conversation state
    if (data.type === 'customer_ended_chat' && data.conversation_id) {
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.conversation_id === data.conversation_id) {
            logger.info('🔚 Customer ended chat: Updating conversation list', {
              conversationId: data.conversation_id,
              visitorName: data.data?.visitor_name,
              autoResolved: data.data?.auto_resolved
            })

            // Show browser notification for customer end chat
            if (typeof window !== 'undefined' && 'Notification' in window) {
              const visitorName = data.data?.visitor_name || 'Customer'
              const title = 'Customer Ended Chat'
              const body = `${visitorName} ended their chat session${data.data?.auto_resolved ? ' (automatically resolved)' : ''}`
              
              // Request notification permission if not already granted
              if (Notification.permission === 'granted') {
                new Notification(title, {
                  body,
                  icon: '/favicon.ico',
                  requireInteraction: false
                })
              } else if (Notification.permission !== 'denied') {
                Notification.requestPermission().then(permission => {
                  if (permission === 'granted') {
                    new Notification(title, {
                      body,
                      icon: '/favicon.ico',
                      requireInteraction: false
                    })
                  }
                })
              }
            }

            return {
              ...conv,
              last_message: `${data.data?.visitor_name || 'Customer'} ended the chat session${data.data?.auto_resolved ? ' (automatically resolved)' : ''}`,
              last_message_time: data.data?.ended_at || data.timestamp,
              updated_at: data.data?.ended_at || data.timestamp,
              status: data.data?.auto_resolved ? 'resolved' : conv.status
            }
          }
          return conv
        })
        
        return updated
      })
    } else if (data.type === 'conversation_status_update' && data.conversation_id) {
      logger.info('🔄 ENHANCED DEBUG: Received conversation_status_update', {
        messageType: data.type,
        conversationId: data.conversation_id,
        statusData: data.data,
        fullMessage: data,
        timestamp: new Date().toISOString()
      })
      
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.conversation_id === data.conversation_id) {
            logger.info('📊 ENHANCED DEBUG: Updating conversation in list', {
              conversationId: data.conversation_id,
              oldStatus: conv.status,
              newStatus: data.data?.status,
              resolvedBy: data.data?.resolved_by,
              beforeUpdate: conv,
              updateData: data.data
            })

            return {
              ...conv,
              status: data.data?.status || conv.status,
              resolved_at: data.data?.resolved_at,
              resolved_by: data.data?.resolved_by,
              updated_at: data.timestamp
            }
          }
          return conv
        })
        
        return updated
      })
    } else if (data.type === 'widget_visitor_disconnected' && data.conversation_id) {
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.conversation_id === data.conversation_id) {
            logger.info('🔌 Widget visitor disconnected: Updating conversation list', {
              conversationId: data.conversation_id,
              visitorName: data.data?.visitor_name || 'Customer'
            })

            // Show browser notification for visitor disconnect
            if (typeof window !== 'undefined' && 'Notification' in window) {
              const visitorName = data.data?.visitor_name || 'Customer'
              const title = 'Customer Disconnected'
              const body = `${visitorName} left the chat session`
              
              // Request notification permission if not already granted
              if (Notification.permission === 'granted') {
                new Notification(title, {
                  body,
                  icon: '/favicon.ico',
                  requireInteraction: false
                })
              } else if (Notification.permission !== 'denied') {
                Notification.requestPermission().then(permission => {
                  if (permission === 'granted') {
                    new Notification(title, {
                      body,
                      icon: '/favicon.ico',
                      requireInteraction: false
                    })
                  }
                })
              }
            }

            return {
              ...conv,
              last_message: `${data.data?.visitor_name || 'Customer'} left the chat session`,
              last_message_time: data.timestamp,
              updated_at: data.timestamp
            }
          }
          return conv
        })
        
        return updated
      })
    } else if (data.type === 'widget_connection_ended' && data.conversation_id) {
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.conversation_id === data.conversation_id) {
            logger.info('🔌 Widget connection ended: Updating conversation list', {
              conversationId: data.conversation_id,
              visitorName: data.data?.visitor_name || 'Customer'
            })

            return {
              ...conv,
              last_message: `${data.data?.visitor_name || 'Customer'} connection ended`,
              last_message_time: data.timestamp,
              updated_at: data.timestamp
            }
          }
          return conv
        })
        
        return updated
      })
    } else if (data.type === 'new_message' && data.conversation_id) {
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.conversation_id === data.conversation_id) {
            logger.info('📱 WhatsApp-style update: Updating conversation list instantly', {
              conversationId: data.conversation_id,
              oldLastMessage: conv.last_message,
              newLastMessage: data.last_message,
              oldUnreadCount: conv.unread_count,
              newUnreadCount: data.unread_count
            })

            // 🎯 CRITICAL FIX: For customer messages, increment unread count by 1 instead of using total
            const newUnreadCount = data.unread_count !== undefined 
              ? (data.unread_count === 0 ? 0 : (conv.unread_count || 0) + 1) // Increment by 1 for new messages
              : (conv.unread_count || 0)

            return {
              ...conv,
              last_message: data.last_message,
              last_message_time: data.last_message_time,
              unread_count: newUnreadCount,
              updated_at: data.timestamp || new Date().toISOString()
            }
          }
          return conv
        })
        
        // Only update if something actually changed to prevent infinite loops
        const hasChanges = updated.some((conv, index) => {
          const original = prev[index]
          return conv.last_message !== original.last_message || 
                 conv.unread_count !== original.unread_count
        })
        
        return hasChanges ? updated : prev
      })
    }
    
    // 🎯 CRITICAL: Handle messages_read events to reset unread count
    if (data.type === 'messages_read' && data.conversation_id) {
      setConversations(prev => 
        prev.map(conv => {
          if (conv.conversation_id === data.conversation_id) {
            logger.info('📖 Messages marked as read: Resetting unread count', {
              conversationId: data.conversation_id,
              oldUnreadCount: conv.unread_count,
              newUnreadCount: 0
            })

            return {
              ...conv,
              unread_count: 0,
              updated_at: data.timestamp || new Date().toISOString()
            }
          }
          return conv
        })
      )
    }
    
    // 🎯 CRITICAL: Handle conversation_update events for status changes (resolve, transfer, etc.)
    if (data.type === 'conversation_update' && data.conversation_id) {
      setConversations(prev => 
        prev.map(conv => {
          if (conv.conversation_id === data.conversation_id) {
            logger.info('🔄 Conversation status updated: Updating conversation list instantly', {
              conversationId: data.conversation_id,
              oldStatus: conv.status,
              newStatus: data.status,
              oldAssignedAgent: conv.assigned_agent_name,
              newAssignedAgent: data.assigned_agent_name
            })

            return {
              ...conv,
              status: data.status,
              assigned_agent_id: data.assigned_agent_id,
              assigned_agent_name: data.assigned_agent_name,
              handler_type: data.handler_type,
              updated_at: data.timestamp || new Date().toISOString()
            }
          }
          return conv
        })
      )
    }
  }, [setConversations, processedEventsRef])
  
  // Real-time hooks (only if authenticated)
  const { unreadCounts, totalUnread } = useUnreadCounts()
  const { conversations: conversationUpdates } = useConversationUpdates()
  const { isConnected } = useRealTimeNotifications()

  // Clear data when user logs out
  useEffect(() => {
    if (!isAuthenticated || !user) {
      logger.debug('📋 Clearing conversation data - user not authenticated')
      setConversations([])
      setError(null)
      setIsLoading(false)
      return
    }
  }, [isAuthenticated, user])

  // Initial load (only for authenticated users!)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return
    }

    const loadInitialConversations = async () => {
      try {
        setIsLoading(true)
        
        // Both agents and company owners use the same endpoint to see all conversations
        const userType = localStorage.getItem('user_type')
        const endpoint = '/conversations/assignments'
        
        const response = await api.get(endpoint)
        
        // Debug: Log the raw API response
        console.log(`🔍 API Response - ${endpoint}:`, {
          totalCount: response.data?.length || 0,
          userType,
          conversations: response.data?.map((conv: any, index: number) => ({
            index,
            id: conv.id,
            conversation_id: conv.conversation_id,
            visitor_name: conv.visitor_name,
            isDuplicate: response.data.findIndex((c: any) => c.id === conv.id) !== index
          }))
        })
        
        setConversations(response.data)
        setError(null)
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load conversations')
        logger.error('Failed to load initial conversations', { error: err })
        
      } finally {
        setIsLoading(false)
      }
    }

    loadInitialConversations()
  }, [isAuthenticated, user]) // Re-run when authentication changes

  // Apply real-time updates to conversations (WhatsApp style)
  useEffect(() => {
    if (conversations.length === 0) return

    setConversations(prev => 
      prev.map(conv => {
        // Apply unread count updates
        const unreadCount = unreadCounts.get(conv.conversation_id) || conv.unread_count || 0
        
        // Apply conversation updates (includes message updates)
        const updates = conversationUpdates.get(conv.conversation_id)
        
        return {
          ...conv,
          ...(updates || {}),
          unread_count: unreadCount
        }
      })
    )
  }, [unreadCounts, conversationUpdates])

  // Create debounced conversation handler outside useEffect 
  const conversationListUpdateHandler = useCallback((data: any) => {
    // Initialize debouncer if not already created
    if (!debouncerRef.current) {
      debouncerRef.current = createWebSocketDebouncer(handleConversationUpdate, 150)
    }
    
    // Create unique key for debouncing: type + conversation_id
    const debounceKey = `${data.type}-${data.conversation_id || 'global'}`
    
    // Log but don't flood with duplicate events
    if (Math.random() < 0.1) { // Only log 10% of events to reduce noise
      logger.debug('🔄 WebSocket event received (debouncing):', {
        type: data.type,
        conversationId: data.conversation_id,
        debounceKey,
        timestamp: new Date().toISOString()
      })
    }
    
    // Use the debouncer
    if (debouncerRef.current) {
      debouncerRef.current.debounce(debounceKey, data)
    }
  }, [handleConversationUpdate])

  // Listen for direct message events to update conversation previews instantly
  useEffect(() => {
    if (!isAuthenticated || !user) return

    // Listen for message_received events from the WebSocket
    const unsubscribeMessageReceived = realTimeNotifications.addEventListener('message_received', (messageData: any) => {
      logger.info('📨 Direct message_received event in conversation list', {
        conversationId: messageData.conversation_id,
        content: messageData.content,
        source: messageData.source,
        timestamp: messageData.timestamp
      })

      // Update conversation list immediately with new message preview
      setConversations(prev => 
        prev.map(conv => {
          if (conv.conversation_id === messageData.conversation_id) {
            const content = messageData.content || messageData.data?.content || messageData.data?.message || ""
            const source = messageData.source || messageData.data?.source || messageData.data?.role || 'unknown'
            
            if (content) {
              const truncatedContent = content.length > 100 ? content.substring(0, 100) + "..." : content
              const formattedMessage = source === 'user' 
                ? `Customer: ${truncatedContent}` 
                : `Agent: ${truncatedContent}`
              
              return {
                ...conv,
                last_message: formattedMessage,
                last_message_time: messageData.timestamp,
                updated_at: messageData.timestamp
              }
            }
          }
          return conv
        })
      )
    })

    // Listen for widget_message_received events from the WebSocket
    const unsubscribeWidgetMessage = realTimeNotifications.addEventListener('widget_message_received', (messageData: any) => {
      logger.info('📨 Direct widget_message_received event in conversation list', {
        conversationId: messageData.conversation_id,
        content: messageData.message_content,
        timestamp: messageData.timestamp
      })

      // Update conversation list immediately with new message preview
      setConversations(prev => 
        prev.map(conv => {
          if (conv.conversation_id === messageData.conversation_id) {
            const content = messageData.message_content || ""
            
            if (content) {
              const truncatedContent = content.length > 100 ? content.substring(0, 100) + "..." : content
              const formattedMessage = `Customer: ${truncatedContent}`
              
              return {
                ...conv,
                last_message: formattedMessage,
                last_message_time: messageData.timestamp,
                updated_at: messageData.timestamp,
                unread_count: messageData.unread_count || conv.unread_count || 0
              }
            }
          }
          return conv
        })
      )
    })
    
    realTimeConversationsService.addEventListener('conversation_list_update', conversationListUpdateHandler)

    return () => {
      unsubscribeMessageReceived()
      unsubscribeWidgetMessage()
      realTimeConversationsService.removeEventListener('conversation_list_update', conversationListUpdateHandler)
      // Cleanup debouncer
      if (debouncerRef.current) {
        debouncerRef.current.cleanup()
        debouncerRef.current = null
      }
    }
  }, [isAuthenticated, user, conversationListUpdateHandler])

  // 🎯 CRITICAL FIX: Listen for new_conversation_assigned events to instantly add conversations
  useEffect(() => {
    if (!isAuthenticated || !user) return

    const handleNewConversationAssigned = async (event: CustomEvent) => {
      const { conversationId, conversationData } = event.detail
      const conversation_id = conversationId
      const visitor_name = conversationData?.visitor_name
      const handoff_reason = conversationData?.handoff_reason
      
      logger.info('🆕 NEW CONVERSATION ASSIGNED: Adding to list immediately', {
        conversationId: conversation_id,
        visitorName: visitor_name,
        handoffReason: handoff_reason,
        timestamp: new Date().toISOString()
      })
      
      // Check if conversation already exists in the list
      const conversationExists = conversations.some(conv => conv.conversation_id === conversation_id)
      
      if (!conversationExists) {
        // Create a temporary conversation entry immediately for instant UI update
        const tempConversation = {
          id: conversation_id,
          conversation_id: conversation_id,
          visitor_name: visitor_name || 'Website Visitor',
          customer_name: visitor_name || 'Website Visitor',
          status: 'pending',
          handoff_reason: handoff_reason,
          created_at: new Date().toISOString(),
          last_message: `Customer: ${handoff_reason}`,
          last_message_time: new Date().toISOString(),
          unread_count: 1,
          priority: 'normal',
          total_messages: 1,
          wait_time: 0,
          assigned_agent_id: null,
          assigned_agent_name: null,
          device_type: 'unknown',
          browser_name: '',
          city: '',
          country: '',
          is_active: true
        }
        
        // Add it to the conversations immediately (prepend to show at top)
        setConversations(prev => [tempConversation, ...prev])
        
        logger.info('✅ NEW CONVERSATION: Added to list instantly', {
          conversationId: conversation_id,
          tempConversation: tempConversation
        })
        
        // Then fetch the complete data from backend to update with accurate information
        try {
          // Small delay to allow backend to process the handoff
          setTimeout(async () => {
            try {
              const response = await api.get(`/conversations/assignments`)
              const fullConversations = response.data
              
              // Find the specific conversation in the response
              const fullConversation = fullConversations.find((conv: any) => 
                conv.conversation_id === conversation_id
              )
              
              if (fullConversation) {
                // Update with full data from backend
                setConversations(prev => 
                  prev.map(conv => 
                    conv.conversation_id === conversation_id ? fullConversation : conv
                  )
                )
                
                logger.info('✅ NEW CONVERSATION: Updated with full backend data', {
                  conversationId: conversation_id,
                  fullConversation: fullConversation
                })
              }
            } catch (error) {
              logger.error('❌ Failed to fetch full conversation details', {
                conversationId: conversation_id,
                error: error instanceof Error ? error.message : 'Unknown error'
              })
              // Keep the temporary entry if fetch fails
            }
          }, 500) // 500ms delay to allow backend processing
          
        } catch (error) {
          logger.error('❌ Failed to setup delayed fetch for new conversation', {
            conversationId: conversation_id,
            error: error instanceof Error ? error.message : 'Unknown error'
          })
        }
      } else {
        logger.info('🔍 NEW CONVERSATION: Already exists in list, skipping addition', {
          conversationId: conversation_id
        })
      }
    }
    
    // Listen for customer ended chat events
    const handleCustomerEndedChat = async (event: CustomEvent) => {
      const { conversationId, sessionId, endedAt, sessionDuration, conversationStatus, autoResolved, visitorName } = event.detail
      
      logger.info('🔚 Customer ended chat notification', {
        conversationId,
        sessionId,
        endedAt,
        sessionDuration,
        conversationStatus,
        autoResolved,
        visitorName
      })
      
      // Update conversation in list
      setConversations(prev => 
        prev.map(conv => {
          if (conv.conversation_id === conversationId) {
            return {
              ...conv,
              last_message: `${visitorName || 'Customer'} ended the chat session${autoResolved ? ' (automatically resolved)' : ''}`,
              last_message_time: endedAt,
              updated_at: endedAt,
              status: autoResolved ? 'resolved' : conv.status
            }
          }
          return conv
        })
      )
    }
    
    logger.debug('🎧 Setting up event listeners in useRealTimeConversationList')
    window.addEventListener('newConversationAssigned', handleNewConversationAssigned as EventListener)
    window.addEventListener('customerEndedChat', handleCustomerEndedChat as EventListener)
    
    return () => {
      logger.debug('🎧 Removing event listeners from useRealTimeConversationList')
      window.removeEventListener('newConversationAssigned', handleNewConversationAssigned as EventListener)
      window.removeEventListener('customerEndedChat', handleCustomerEndedChat as EventListener)
    }
  }, [isAuthenticated, user, conversations]) // Include conversations in deps to check for duplicates

  // Manual refresh function (for error recovery only)
  const refresh = useCallback(async () => {
    // Only refresh if WebSocket is disconnected (for error recovery)
    if (!isConnected) {
      setIsLoading(true)
      try {
        const endpoint = '/conversations/assignments'
        
        logger.info('🔄 Refreshing conversation list (WebSocket disconnected)', {
          isConnected,
          reason: 'WebSocket disconnected'
        })
        
        const response = await api.get(endpoint)
        
        setConversations(response.data)
        setError(null)
        
        logger.info('✅ Conversation list refreshed successfully', {
          count: response.data?.length || 0
        })
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to refresh conversations')
        logger.error('❌ Failed to refresh conversation list', err)
        
      } finally {
        setIsLoading(false)
      }
    } else {
      logger.debug('🔄 Skipping refresh - WebSocket connected, using real-time updates')
    }
  }, [isConnected])

  return {
    conversations,
    isLoading,
    error,
    totalUnread,
    refresh, // For error recovery when WebSocket is disconnected
    isRealTime: isConnected,
    setConversations // Expose setConversations for direct updates
  }
}